import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faClone, faSave } from '@fortawesome/free-solid-svg-icons';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { es, enUS } from 'date-fns/locale';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 352px; // Increased from 320px to 352px (10% increase)
  width: 100%;
`;

const ModalTitle = styled.h2`
  text-align: center;
  margin-bottom: 20px;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

const SettingsButton = styled.button`
  padding: 10px 0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30%;
  font-size: 14px;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled(SettingsButton)`
  background-color: #dc3545;
  color: white;
`;

const CloneButton = styled(SettingsButton)`
  background-color: #ffc107;
  color: black;
`;

const SaveButton = styled(SettingsButton)`
  background-color: #28a745;
  color: white;
`;

const ButtonIcon = styled(FontAwesomeIcon)`
  margin-right: 5px;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 10px 0;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 50px;
  height: 24px;
  background-color: ${props => props.checked ? '#28a745' : '#ccc'};
  border-radius: 12px;
  transition: background-color 0.3s ease;
  margin-left: 10px;

  &:before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: white;
    top: 2px;
    left: ${props => props.checked ? '28px' : '2px'};
    transition: left 0.3s ease;
  }
`;

const ToggleInput = styled.input`
  display: none;
`;

const SettingsModal = ({ 
  show, 
  onClose, 
  title, 
  dateRange, 
  isPublic,
  onTitleChange, 
  onDateChange,
  onPublicChange,
  onSave, 
  onDelete, 
  onClone, 
  hasUnsavedChanges 
}) => {
  const { language } = useLanguage();
  const t = translations[language].pages.myTrips.settings;

  if (!show) {
    return null;
  }

  const defaultMonth = dateRange?.from || new Date();

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{t.title}</ModalTitle>
        <ToggleContainer>
          <ToggleLabel>
            {t.makePublic}
            <ToggleInput
              type="checkbox"
              checked={isPublic}
              onChange={(e) => onPublicChange(e.target.checked)}
            />
            <ToggleSwitch checked={isPublic} />
          </ToggleLabel>
        </ToggleContainer>
        <InputGroup>
          <Input 
            type="text" 
            value={title} 
            onChange={onTitleChange} 
            placeholder={t.tripName} 
          />
        </InputGroup>
        <InputGroup>
          <DayPicker
            mode="range"
            selected={dateRange}
            onSelect={onDateChange}
            numberOfMonths={1}
            defaultMonth={defaultMonth}
            locale={language === 'es' ? es : enUS}
          />
        </InputGroup>
        <ButtonGroup>
          <DeleteButton onClick={onDelete}>
            <ButtonIcon icon={faTrash} /> {t.delete}
          </DeleteButton>
          <CloneButton onClick={onClone}>
            <ButtonIcon icon={faClone} /> {t.clone}
          </CloneButton>
          <SaveButton onClick={onSave} disabled={!hasUnsavedChanges}>
            <ButtonIcon icon={faSave} /> {t.save}
          </SaveButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SettingsModal;
